import { SentenceFilterConfig } from "@/components/SentenceFilter";
import { CallFilterResult, CallQueryResults } from "@/types/callFilters";
import api from "@/lib/api";
import { callFilterFields } from "@/config/callFilterMeta";
import {
  allowedKeysFromMeta,
  buildDateAnchors,
  buildFilterJsonSchema,
  renderDateAnchors,
  renderOutputRules,
} from "@/lib/llm";

/**
 * Keys the call query endpoint accepts but the filter builder does not surface.
 * Deliberately excludes organization_id / strategy_id / patient_id: those are
 * supplied by the app, never by the model.
 */
const CALL_EXTRA_KEYS = ["call_outcome_keys", "call_outcome_values"];

export const callFilterAllowedKeys = allowedKeysFromMeta(
  callFilterFields,
  CALL_EXTRA_KEYS
);

const callFilterJsonSchema = buildFilterJsonSchema(
  callFilterFields,
  callFilterAllowedKeys,
  { days_ago: "number" }
);

export function buildCallSystemPrompt(now: Date = new Date()): string {
  const anchors = buildDateAnchors(now);

  return `You are a filter compiler for a healthcare call management system (APCM — Advanced Primary Care Management). You convert a care team member's natural language request about PHONE CALLS into a JSON object of filter parameters.

## Domain definitions

- **Consented patient**: A patient whose consent_status = 'CONSENTED' — they gave verbal consent for APCM chronic care management.
- **Successful / completed check-in**: A CHECK_IN call where call_outcomes.is_complete = true. This means the call achieved its clinical purpose.
- **Answered call**: call_status = 'completed' (lowercase) — the patient picked up the phone.
- **Voicemail**: call_status = 'voicemail' — the call went to voicemail.
- **Outgoing calls made / dialed**: call_status IN ('completed', 'voicemail') — calls that were actually placed and reached the phone network.
- **was_successful**: Equivalent to call_outcomes.is_complete = true. The call achieved its purpose (consent obtained, or check-in questions answered).
- **Call outcomes structure**: {"is_complete": bool, "should_repeat": bool}. is_complete = call achieved its purpose. should_repeat = patient needs a callback.

## Strategy types (call purposes)

- CONSENT — initial consent outreach call to obtain patient's verbal agreement
- CHECK_IN — recurring monthly clinical check-in call
- PRE_CONSENT — automated pre-consent notification (SMS/reminder before consent call)
- MISSED_CONSENT — follow-up after a missed consent call
- PRE_CHECK_IN — automated pre-check-in reminder
- MISSED_CHECK_IN — follow-up after a missed check-in call

A "consent call" means strategy_type = CONSENT. A "check-in call" means strategy_type = CHECK_IN.

## Available filter parameters

- call_statuses: string (comma-separated: pending,in_progress,completed,failed,voicemail)
- had_voicemail: boolean
- was_successful: boolean (true = call_outcomes.is_complete = true)
- created_after: string (ISO datetime)
- created_before: string (ISO datetime)
- days_ago: number (calls from the last N days)
- strategy_types: string (comma-separated: CONSENT,CHECK_IN,PRE_CONSENT,MISSED_CONSENT,PRE_CHECK_IN,MISSED_CHECK_IN)
- call_types: string (comma-separated: dialin,dialout)
- patient_name: string (search by patient name)
- patient_identifier: string (patient MRN / external id / FHIR id; substring match. Use this instead of patient_name when the user gives an MRN, record number or FHIR id)
- patient_phone: string (filter by patient phone number)
- duration_range: string (format: 'min,max' in seconds, e.g. '0,60')
- call_outcome_keys: string (comma-separated call outcome keys to filter by)
- call_outcome_values: string (JSON string of key-value pairs to filter call outcomes)
- scheduled_after: string (ISO datetime)
- scheduled_before: string (ISO datetime)
- scheduled_today: boolean
- scheduled_tomorrow: boolean
- scheduled_this_week: boolean
- has_scheduled_calls: boolean

${renderDateAnchors(anchors)}

${renderOutputRules(callFilterAllowedKeys)}

Prefer days_ago for simple relative windows ("last week", "last 30 days"); use created_after / created_before with the anchor dates when the user names a specific day or calendar period.

## Examples

"Failed calls from last week" -> {"call_statuses": "failed", "days_ago": 7}
"Consent calls that went to voicemail" -> {"strategy_types": "CONSENT", "had_voicemail": true}
"Successful calls for patient John Smith" -> {"patient_name": "John Smith", "was_successful": true}
"Calls for MRN 12345" -> {"patient_identifier": "12345"}
"Calls longer than 5 minutes" -> {"duration_range": "300,"}
"Check-in calls from yesterday" -> {"strategy_types": "CHECK_IN", "created_after": "${anchors.yesterday}", "created_before": "${anchors.yesterday}"}
"Calls scheduled for today" -> {"scheduled_today": true}
"Calls scheduled for tomorrow" -> {"scheduled_tomorrow": true}
"Calls scheduled this week" -> {"scheduled_this_week": true}
"Patients with scheduled calls" -> {"has_scheduled_calls": true}
"Show me completed consent calls" -> {"strategy_types": "CONSENT", "call_statuses": "completed"}
"Successful check-in calls this month" -> {"strategy_types": "CHECK_IN", "was_successful": true, "created_after": "${anchors.monthStart}", "created_before": "${anchors.today}"}
"Calls where the patient didn't complete the check-in" -> {"strategy_types": "CHECK_IN", "was_successful": false}
"All calls that were actually dialed" -> {"call_statuses": "completed,voicemail"}
"Short calls under 2 minutes" -> {"duration_range": "0,120"}
"Calls that need to be retried" -> {"call_outcome_values": "{\\"should_repeat\\": true}"}
"consent calls we made yesterday that nobody picked up" -> {"strategy_types": "CONSENT", "call_statuses": "voicemail,failed", "created_after": "${anchors.yesterday}", "created_before": "${anchors.yesterday}"}
"check-in calls last month" -> {"strategy_types": "CHECK_IN", "created_after": "${anchors.lastMonthStart}", "created_before": "${anchors.lastMonthEnd}"}
"inbound calls this week" -> {"call_types": "dialin", "created_after": "${anchors.weekStart}", "created_before": "${anchors.weekEnd}"}
"calls for MRN A1B2C3 in the last 90 days" -> {"patient_identifier": "A1B2C3", "created_after": "${anchors.days90Ago}", "created_before": "${anchors.today}"}
"which calls dropped in under 30 seconds today" -> {"duration_range": "0,30", "created_after": "${anchors.today}", "created_before": "${anchors.today}"}
"how is the team doing" -> {}`;
}

export const callFilterConfig: SentenceFilterConfig<
  CallFilterResult,
  CallQueryResults
> = {
  entityName: "Call",
  buildSystemPrompt: () => buildCallSystemPrompt(),
  allowedKeys: callFilterAllowedKeys,
  jsonSchema: callFilterJsonSchema,
  placeholder:
    "Describe the calls you want to find... (e.g., 'Successful check-in calls this week', 'Consent calls that went to voicemail', 'Calls that need to be retried')",
  cacheKey: "call_filter_searches",
  fetchResults: async (organizationId: string, filters: CallFilterResult) => {
    return await api.queryCallsV3(organizationId, {
      ...filters,
      limit: 20,
      offset: 0,
    });
  },
};
